import type { QueryClient } from 'react-query';
import type { QueryError } from '@actions/services/firestore/browser/errors';
import type { AdminActorRef, AdminServiceBillingPlan } from '@/hooks/admin/services/types';
import type { LinkedOption } from '@/components/LinkedOptionCard/types';

export type OrganizationServiceStatus = 'active' | 'archived';

export type OrganizationServiceSourceAvailability = 'available' | 'archived';

export type OrganizationServiceLifecycleOperation = 'archive' | 'restore';

export type OrganizationServiceWarningCode =
    | 'SERVICE_PLAYBOOK_NOT_FOUND'
    | 'SERVICE_PLAYBOOK_ARCHIVED'
    | 'SERVICE_PLAYBOOK_UNPUBLISHED';

export type OrganizationServiceWarning = {
    code: OrganizationServiceWarningCode;
    playbookId: string;
};

export type OrganizationServiceSource = {
    availability: OrganizationServiceSourceAvailability;
    playbookIds: string[];
};

/** External Organization Services HTTP DTO. Timestamps are ISO strings. */
export type OrganizationService = {
    id: string;
    schemaVersion: number;
    revision: number;
    organizationId: string;
    serviceBlueprintId: string;
    sourceBlueprintRevision: number;
    name: string;
    description: string;
    iconName: string;
    billingAtAssignment: AdminServiceBillingPlan;
    billing: AdminServiceBillingPlan;
    requirementsMarkdown: string;
    status: OrganizationServiceStatus;
    archivedBy: AdminActorRef | null;
    archivedAt: string | null;
    evidenceCount: number;
    workflowCount: number;
    promptCount: number;
    assignedBy: AdminActorRef;
    createdBy: AdminActorRef;
    updatedBy: AdminActorRef;
    assignedAt: string;
    createdAt: string;
    updatedAt: string;
    source: OrganizationServiceSource;
};

/** Normalized live model. Same shape as the HTTP DTO; unions are required. */
export type LiveOrganizationService = OrganizationService;

export type MapOrganizationServiceToLinkedOptionParams = {
    instance: LiveOrganizationService;
};

export type MapOrganizationServiceToLinkedOptionResult = LinkedOption;

export type CollectOrganizationServiceLinkedOptionsParams = {
    instances: LiveOrganizationService[];
    selectedIds: string[];
    fallbackDetail: string;
};

export type NormalizeOrganizationServiceIdsParams = {
    organizationServiceInstanceIds: string[] | undefined;
};

export type MapReplaceOrganizationServicesErrorParams = {
    error: unknown;
};

export type ReplaceOrganizationServicesMappedError = {
    message: string;
};

export type InvalidateOrganizationServiceListQueriesParams = {
    queryClient: QueryClient;
    organizationId: string;
};

export type OrganizationServiceEnvelope = {
    data: {
        instance: OrganizationService;
    };
};

export type OrganizationServicesListEnvelope = {
    data: {
        instances: OrganizationService[];
    };
};

export type OrganizationServiceAssignmentEnvelope = {
    data: {
        instance: OrganizationService;
        warnings: OrganizationServiceWarning[];
    };
};

export type LiveOrganizationServiceEnvelope = {
    data: {
        instance: LiveOrganizationService;
    };
};

export type LiveOrganizationServicesListEnvelope = {
    data: {
        instances: LiveOrganizationService[];
    };
};

export type OrganizationServiceUnsupportedError = {
    code: 'ORGANIZATION_SERVICE_DATA_VERSION_UNSUPPORTED';
    message: string;
};

export type OrganizationServicesListQueryKey = ['admin-organization-services', string, boolean];

export type OrganizationServiceDetailQueryKey = ['admin-organization-services', string, string];

export type BuildOrganizationServicesListQueryKeyParams = {
    organizationId: string;
    includeArchived: boolean;
};

export type BuildOrganizationServiceDetailQueryKeyParams = {
    organizationId: string;
    instanceId: string;
};

export type NormalizeOrganizationServiceFromApiParams = {
    dto: OrganizationService;
};

export type NormalizeOrganizationServiceBillingFromApiParams = {
    billing: unknown;
};

export type NormalizeOrganizationServiceWarningsFromApiParams = {
    warnings: unknown;
};

export type NormalizeOrganizationServiceActorFromApiParams = {
    actor: unknown;
};

export type NormalizeOrganizationServiceSourceFromApiParams = {
    source: unknown;
};

export const ORGANIZATION_SERVICE_UNSUPPORTED_MESSAGE =
    'This data version is not supported—refresh or update';

export const ORGANIZATION_SERVICE_REQUIREMENTS_AUTOSAVE_DEBOUNCE_MS = 600;

export const ORGANIZATION_SERVICES_INCLUDE_ARCHIVED_VARIANTS = [false, true] as const;

export type ThrowUnsupportedOrganizationServiceParams = Record<string, never>;

export type BuildOrganizationServicesCollectionPathParams = {
    organizationId: string;
};

export type BuildOrganizationServiceInstancePathParams = {
    organizationId: string;
    instanceId: string;
};

export type CollectUnsupportedOrganizationServiceParams = {
    caughtError: unknown;
};

export type ExtractOrganizationServiceRevisionConflictParams = {
    error: unknown;
};

export type ExtractedOrganizationServiceRevisionConflict = {
    currentInstance: LiveOrganizationService;
};

export type OrganizationServiceCacheWriteParams = {
    queryClient: QueryClient;
    instance: LiveOrganizationService;
};

export type PatchOrganizationServiceInCacheParams = OrganizationServiceCacheWriteParams;

export type AppendOrganizationServiceInListCacheParams = OrganizationServiceCacheWriteParams;

export type WriteOrganizationServicesListCacheParams = {
    queryClient: QueryClient;
    listKey: OrganizationServicesListQueryKey;
    instances: LiveOrganizationService[];
};

export type VisitOrganizationServicesCachedListParams = {
    includeArchived: boolean;
    listKey: OrganizationServicesListQueryKey;
    cachedList: LiveOrganizationServicesListEnvelope;
};

export type VisitOrganizationServicesListCachesParams = {
    queryClient: QueryClient;
    organizationId: string;
    onCachedList: (params: VisitOrganizationServicesCachedListParams) => void;
};

export type ReplaceOrganizationServiceListRowParams = {
    instances: LiveOrganizationService[];
    instance: LiveOrganizationService;
};

export type ShouldHideArchivedOrganizationServiceFromListParams = {
    status: OrganizationServiceStatus;
    includeArchived: boolean;
};

export type CollectNormalizedOrganizationServicesParams = {
    instances: OrganizationService[];
};

export type CollectNormalizedOrganizationServicesResult = {
    instances: LiveOrganizationService[];
    unsupportedError: OrganizationServiceUnsupportedError | undefined;
};

export type CollectNormalizedOrganizationServiceParams = {
    instance: OrganizationService;
};

export type CollectNormalizedOrganizationServiceResult = {
    instance: LiveOrganizationService | null;
    unsupportedError: OrganizationServiceUnsupportedError | undefined;
};

export type ResolveOrganizationServiceAssignmentKeyParams = {
    pendingKeys: Map<string, string>;
    blueprintId: string;
};

export type CreateTestOrganizationServiceParams = {
    id?: string;
    organizationId?: string;
    serviceBlueprintId?: string;
    name?: string;
    status?: OrganizationServiceStatus;
    revision?: number;
    assignedAt?: string;
    workflowCount?: number;
    promptCount?: number;
};

export type OrganizationServiceWorkflowPublicationState = 'published' | 'draft';

export type OrganizationServiceWorkflow = {
    id: string;
    name: string;
    enabled: boolean;
    publicationState: OrganizationServiceWorkflowPublicationState;
    publishedVersionId: string | null;
    draftVersionId: string | null;
};

export type LiveOrganizationServiceWorkflow = OrganizationServiceWorkflow;

export type OrganizationServiceWorkflowsEnvelope = {
    data: {
        workflows: OrganizationServiceWorkflow[];
        workflowCount: number;
    };
    nextCursor: string | null;
};

export type OrganizationServiceWorkflowsQueryKey = [
    'admin-organization-services',
    string,
    string,
    'workflows',
    string | null,
];

export type BuildOrganizationServiceWorkflowsPathParams = {
    organizationId: string;
    instanceId: string;
    cursor: string | null;
};

export type BuildOrganizationServiceWorkflowsQueryKeyParams = {
    organizationId: string;
    instanceId: string;
    cursor: string | null;
};

export type NormalizeOrganizationServiceWorkflowFromApiParams = {
    dto: OrganizationServiceWorkflow;
};

export type CollectNormalizedOrganizationServiceWorkflowsParams = {
    workflows: OrganizationServiceWorkflow[];
};

export type CollectNormalizedOrganizationServiceWorkflowsResult = {
    workflows: LiveOrganizationServiceWorkflow[];
    unsupportedError: OrganizationServiceUnsupportedError | undefined;
};

export type UseListOrganizationServiceWorkflowsParams = {
    organizationId: string;
    instanceId: string;
    cursor: string | null;
    enabled: boolean;
};

export type UseListOrganizationServiceWorkflowsResult = {
    workflows: LiveOrganizationServiceWorkflow[];
    workflowCount: number | null;
    nextCursor: string | null;
    isLoadingWorkflows: boolean;
    error: QueryError | OrganizationServiceUnsupportedError | undefined;
    refetch: () => void;
};

export type InvalidateOrganizationServiceWorkflowsQueriesParams = {
    queryClient: QueryClient;
    organizationId: string;
};

export type InvalidateOrganizationServicesQueriesParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
};

export type InvalidateAllOrganizationServicesQueriesParams = {
    queryClient: QueryClient;
};

export const ORGANIZATION_SERVICES_QUERY_KEY_ROOT = 'admin-organization-services';

export const ORGANIZATION_SERVICES_LIVE_QUERY_OPTIONS = {
    staleTime: 0,
    refetchOnMount: 'always' as const,
};

export type AssignOrganizationServiceRequest = {
    serviceBlueprintId: string;
    idempotencyKey: string;
};

export type ReplaceOrganizationServiceBillingRequest = {
    expectedRevision: number;
    billing: AdminServiceBillingPlan;
};

export type ReplaceOrganizationServiceRequirementsRequest = {
    expectedRevision: number;
    requirementsMarkdown: string;
};

export type ChangeOrganizationServiceLifecycleRequest = {
    operation: OrganizationServiceLifecycleOperation;
    expectedRevision: number;
};

export type OrganizationServiceMutationErrorParams = {
    error: unknown;
    currentInstance: LiveOrganizationService | null;
};

export type OrganizationServiceMutationSuccessParams = {
    instance: LiveOrganizationService;
};

export type OrganizationServiceAssignmentSuccessParams = {
    instance: LiveOrganizationService;
    warnings: OrganizationServiceWarning[];
};

export type ApplyOrganizationServiceMutationErrorParams = {
    error: unknown;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type ApplyOrganizationServiceMutationSuccessParams = {
    queryClient: QueryClient;
    response: OrganizationServiceEnvelope;
    onSuccess: (params: OrganizationServiceMutationSuccessParams) => void;
    listCacheStrategy: 'patch' | 'append';
};

export type ApplyOrganizationServiceAssignmentSuccessParams = {
    queryClient: QueryClient;
    response: OrganizationServiceAssignmentEnvelope;
    onSuccess: (params: OrganizationServiceAssignmentSuccessParams) => void;
};

export type UseListOrganizationServicesParams = {
    organizationId: string;
    includeArchived: boolean;
};

export type UseListOrganizationServicesResult = {
    instances: LiveOrganizationService[];
    isLoadingInstances: boolean;
    error: QueryError | OrganizationServiceUnsupportedError | undefined;
    refetch: () => void;
};

export type UseGetOrganizationServiceParams = {
    organizationId: string;
    instanceId: string;
};

export type UseGetOrganizationServiceResult = {
    instance: LiveOrganizationService | null;
    isLoadingInstance: boolean;
    error: QueryError | OrganizationServiceUnsupportedError | undefined;
    refetch: () => void;
};

export type UseAssignOrganizationServiceParams = {
    onSuccess: (params: OrganizationServiceAssignmentSuccessParams) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type AssignOrganizationServiceParams = {
    organizationId: string;
    serviceBlueprintId: string;
    idempotencyKey: string;
};

export type UseAssignOrganizationServiceResult = {
    assignOrganizationService: (params: AssignOrganizationServiceParams) => void;
    isAssigningOrganizationService: boolean;
};

export type UseReplaceOrganizationServiceBillingParams = {
    onSuccess: (params: OrganizationServiceMutationSuccessParams) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type ReplaceOrganizationServiceBillingParams = {
    organizationId: string;
    instanceId: string;
    expectedRevision: number;
    billing: AdminServiceBillingPlan;
};

export type UseReplaceOrganizationServiceBillingResult = {
    replaceOrganizationServiceBilling: (params: ReplaceOrganizationServiceBillingParams) => void;
    isReplacingBilling: boolean;
};

export type UseReplaceOrganizationServiceRequirementsParams = {
    onSuccess: (params: OrganizationServiceMutationSuccessParams) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type ReplaceOrganizationServiceRequirementsParams = {
    organizationId: string;
    instanceId: string;
    expectedRevision: number;
    requirementsMarkdown: string;
};

export type UseReplaceOrganizationServiceRequirementsResult = {
    replaceOrganizationServiceRequirements: (
        params: ReplaceOrganizationServiceRequirementsParams
    ) => void;
    isReplacingRequirements: boolean;
};

export type UseChangeOrganizationServiceLifecycleParams = {
    onSuccess: (params: OrganizationServiceMutationSuccessParams) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type ChangeOrganizationServiceLifecycleParams = {
    organizationId: string;
    instanceId: string;
    expectedRevision: number;
    operation: OrganizationServiceLifecycleOperation;
};

export type UseChangeOrganizationServiceLifecycleResult = {
    changeOrganizationServiceLifecycle: (params: ChangeOrganizationServiceLifecycleParams) => void;
    isChangingLifecycle: boolean;
};

export type OrganizationServiceRequirementsAutosaveStatus =
    | 'idle'
    | 'saving'
    | 'saved'
    | 'error'
    | 'retry';

export type UseOrganizationServiceRequirementsAutosaveParams = {
    organizationId: string;
    instanceId: string;
    revision: number;
    persistedMarkdown: string;
    draftMarkdown: string;
};

export type UseOrganizationServiceRequirementsAutosaveResult = {
    status: OrganizationServiceRequirementsAutosaveStatus;
    retry: () => void;
};

export type PersistOrganizationServiceRequirementsDraftParams = {
    nextRevision: number;
    nextStatus: OrganizationServiceRequirementsAutosaveStatus;
};

export type IsOrganizationServiceNotFoundErrorParams = {
    error: unknown;
};

export type IsUncertainOrganizationServiceAssignmentErrorParams = {
    error: unknown;
};

export type GetOrganizationServiceErrorMessageParams = {
    error: unknown;
};

export type CreateOrganizationServiceAssignmentKeyParams = Record<string, never>;

export type SortOrganizationServicesByAssignedAtParams = {
    instances: LiveOrganizationService[];
};

export type ShouldAutosaveOrganizationServiceRequirementsParams = {
    draftMarkdown: string;
    lastSavedMarkdown: string;
};

export type ResolveOrganizationServiceRequirementsConflictResendParams = {
    conflictResendUsed: boolean;
    currentInstance: LiveOrganizationService | null;
};

export type OrganizationServiceEvidenceKind =
    | 'image'
    | 'markdown'
    | 'text'
    | 'audio'
    | 'pdf'
    | 'video';

export type OrganizationServiceEvidenceUrlDisposition = 'inline' | 'attachment';

export type OrganizationServiceEvidence = {
    id: string;
    displayName: string;
    kind: OrganizationServiceEvidenceKind;
    mimeType: string;
    byteSize: number;
    createdBy: AdminActorRef;
    createdAt: string;
    updatedAt: string;
};

export type LiveOrganizationServiceEvidence = OrganizationServiceEvidence;

export type OrganizationServiceEvidenceListEnvelope = {
    data: {
        evidence: OrganizationServiceEvidence[];
    };
};

export type OrganizationServiceEvidenceUploadEnvelope = {
    data: {
        evidence: OrganizationServiceEvidence;
    };
};

export type OrganizationServiceEvidenceRemoveEnvelope = {
    data: {
        evidenceId: string;
    };
};

export type OrganizationServiceEvidenceUrlEnvelope = {
    data: {
        url: string;
        expiresAt: string;
        disposition: OrganizationServiceEvidenceUrlDisposition;
    };
};

export type LiveOrganizationServiceEvidenceListEnvelope = {
    data: {
        evidence: LiveOrganizationServiceEvidence[];
    };
};

export type OrganizationServiceEvidenceListQueryKey = [
    'admin-organization-services',
    string,
    string,
    'evidence',
];

export type BuildOrganizationServiceEvidenceCollectionPathParams = {
    organizationId: string;
    instanceId: string;
};

export type BuildOrganizationServiceEvidenceItemPathParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
};

export type BuildOrganizationServiceEvidenceUrlPathParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
    disposition: OrganizationServiceEvidenceUrlDisposition;
};

export type BuildOrganizationServiceEvidenceListQueryKeyParams = {
    organizationId: string;
    instanceId: string;
};

export type NormalizeOrganizationServiceEvidenceFromApiParams = {
    dto: OrganizationServiceEvidence;
};

export type IsHiddenOrganizationServiceEvidenceRecordParams = {
    dto: unknown;
};

export type CollectNormalizedOrganizationServiceEvidenceListParams = {
    evidence: unknown[];
};

export type CollectNormalizedOrganizationServiceEvidenceListResult = {
    evidence: LiveOrganizationServiceEvidence[];
    unsupportedError: OrganizationServiceUnsupportedError | undefined;
};

export type WriteOrganizationServiceEvidenceListCacheParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
    evidence: LiveOrganizationServiceEvidence[];
};

export type ApplyOrganizationServiceEvidenceListCacheParams =
    WriteOrganizationServiceEvidenceListCacheParams;

export type UpsertOrganizationServiceEvidenceInListCacheParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
    evidence: LiveOrganizationServiceEvidence;
};

export type RemoveOrganizationServiceEvidenceFromListCacheParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
    evidenceId: string;
};

export type OrganizationServiceEvidenceUploadProgress = {
    loadedBytes: number;
    totalBytes: number;
    ratio: number;
};

export type UploadOrganizationServiceEvidenceRequestParams = {
    organizationId: string;
    instanceId: string;
    expectedRevision: number;
    idempotencyKey: string;
    file: File;
    onProgress: (progress: OrganizationServiceEvidenceUploadProgress) => void;
    signal: AbortSignal;
};

export type OrganizationServiceEvidenceRequestErrorParams = {
    code: string;
    message: string;
    status: number;
    data: unknown;
};

export type CreateOrganizationServiceEvidenceIdempotencyKeyParams = Record<string, never>;

export type FetchOrganizationServiceInstanceParams = {
    organizationId: string;
    instanceId: string;
};

export type FetchOrganizationServiceEvidenceUrlParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
    disposition: OrganizationServiceEvidenceUrlDisposition;
};

export type ResolvedOrganizationServiceEvidenceUrl = {
    url: string;
    expiresAt: string;
    disposition: OrganizationServiceEvidenceUrlDisposition;
};

export type OrganizationServiceEvidenceUrlCacheKey = string;

export type BuildOrganizationServiceEvidenceUrlCacheKeyParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
    disposition: OrganizationServiceEvidenceUrlDisposition;
};

export type IsOrganizationServiceEvidenceUrlExpiredParams = {
    expiresAt: string;
};

export type ResolveOrganizationServiceEvidenceUrlParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
    disposition: OrganizationServiceEvidenceUrlDisposition;
};

export type ReconcileOrganizationServiceEvidenceAfterMutationParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
};

export type ReconcileOrganizationServiceEvidenceAfterUploadParams =
    ReconcileOrganizationServiceEvidenceAfterMutationParams & {
        evidence: LiveOrganizationServiceEvidence;
    };

export type ReconcileOrganizationServiceEvidenceAfterRemoveParams =
    ReconcileOrganizationServiceEvidenceAfterMutationParams & {
        evidenceId: string;
    };

export type ApplyOrganizationServiceEvidenceMutationConflictParams = {
    queryClient: QueryClient;
    error: unknown;
};

export type UseListOrganizationServiceEvidenceParams = {
    organizationId: string;
    instanceId: string;
};

export type UseListOrganizationServiceEvidenceResult = {
    evidence: LiveOrganizationServiceEvidence[];
    isLoadingEvidence: boolean;
    error: QueryError | OrganizationServiceUnsupportedError | undefined;
    refetch: () => void;
};

export type UseGetOrganizationServiceEvidenceUrlResult = {
    resolveOrganizationServiceEvidenceUrl: (
        params: ResolveOrganizationServiceEvidenceUrlParams
    ) => Promise<ResolvedOrganizationServiceEvidenceUrl>;
};

export type UseRemoveOrganizationServiceEvidenceParams = {
    onSuccess: (params: { evidenceId: string; instance: LiveOrganizationService }) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type RemoveOrganizationServiceEvidenceParams = {
    organizationId: string;
    instanceId: string;
    evidenceId: string;
    expectedRevision: number;
};

export type UseRemoveOrganizationServiceEvidenceResult = {
    removeOrganizationServiceEvidence: (params: RemoveOrganizationServiceEvidenceParams) => void;
    isRemovingEvidence: boolean;
};

export type UseUploadOrganizationServiceEvidenceParams = {
    onSuccess: (params: {
        evidence: LiveOrganizationServiceEvidence;
        instance: LiveOrganizationService;
    }) => void;
    onError: (params: OrganizationServiceMutationErrorParams) => void;
};

export type UploadOrganizationServiceEvidenceParams = {
    organizationId: string;
    instanceId: string;
    expectedRevision: number;
    idempotencyKey: string;
    file: File;
    onProgress: (progress: OrganizationServiceEvidenceUploadProgress) => void;
    signal: AbortSignal;
};

export type UseUploadOrganizationServiceEvidenceResult = {
    uploadOrganizationServiceEvidence: (
        params: UploadOrganizationServiceEvidenceParams
    ) => Promise<void>;
};

export type OrganizationServiceEvidenceUploadQueueStatus =
    | 'queued'
    | 'uploading'
    | 'complete'
    | 'error';

export type OrganizationServiceEvidenceUploadQueueItem = {
    id: string;
    file: File;
    idempotencyKey: string;
    status: OrganizationServiceEvidenceUploadQueueStatus;
    progressRatio: number;
    errorMessage: string | null;
};

export type UseOrganizationServiceEvidenceUploadQueueParams = {
    organizationId: string;
    instanceId: string;
    revision: number;
};

export type EnqueueOrganizationServiceEvidenceFilesParams = {
    files: File[];
};

export type RetryOrganizationServiceEvidenceUploadParams = {
    itemId: string;
};

export type UseOrganizationServiceEvidenceUploadQueueResult = {
    items: OrganizationServiceEvidenceUploadQueueItem[];
    enqueueFiles: (params: EnqueueOrganizationServiceEvidenceFilesParams) => void;
    retryItem: (params: RetryOrganizationServiceEvidenceUploadParams) => void;
};

export type WriteOrganizationServiceEvidenceUploadQueueItemsParams = {
    nextItems: OrganizationServiceEvidenceUploadQueueItem[];
};

export type ReplaceOrganizationServiceEvidenceUploadQueueItemParams = {
    itemId: string;
    changes: {
        status: OrganizationServiceEvidenceUploadQueueStatus | null;
        progressRatio: number | null;
        errorMessage: string | null;
    };
};

export type CreateTestOrganizationServiceEvidenceParams = {
    id?: string;
    displayName?: string;
    kind?: OrganizationServiceEvidenceKind;
    mimeType?: string;
    byteSize?: number;
};

export type ReadOrganizationServiceEvidenceListCacheParams = {
    queryClient: QueryClient;
    organizationId: string;
    instanceId: string;
};

export type ParseOrganizationServiceJsonResponseParams = {
    response: Response;
};

export type CreateOrganizationServiceRequestHeadersParams = Record<string, never>;

export type FormatAssignedDateParams = {
    assignedAt: string;
};
